import React from 'react';

// =============================================================================
// MODAL COMPONENTS
// =============================================================================

/**
 * Join Room Modal Component
 */
export const JoinRoomModal = ({
  isOpen,
  roomId,
  setRoomId,
  playerName,
  setPlayerName,
  errorMessage,
  isLoading,
  onJoinRoom,
  onClose
}: {
  isOpen: boolean;
  roomId: string;
  setRoomId: (value: string) => void;
  playerName: string;
  setPlayerName: (value: string) => void;
  errorMessage: string;
  isLoading: boolean;
  onJoinRoom: () => void;
  onClose: () => void;
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4 border border-gray-600">
        <h2 className="text-2xl font-bold text-white mb-6 text-center">
          🃏 Join Poker Room
        </h2>
        
        <div className="space-y-4">
          <div>
            <label htmlFor="roomId" className="block text-sm font-medium text-gray-300 mb-2">
              Room ID
            </label>
            <input
              id="roomId"
              type="text"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter room ID (e.g. 01)"
              autoFocus
            />
          </div>
          
          <div>
            <label htmlFor="playerName" className="block text-sm font-medium text-gray-300 mb-2">
              Your Name
            </label>
            <input
              id="playerName"
              type="text"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter your name"
            />
          </div>
          
          {errorMessage && (
            <div className="text-red-400 text-sm text-center">
              {errorMessage}
            </div>
          )}
          
          <div className="flex space-x-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onJoinRoom}
              disabled={isLoading}
              className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:bg-gray-600"
            >
              {isLoading ? 'Joining...' : 'Join Room'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Reconnect Modal Component
 */
export const ReconnectModal = ({
  isOpen,
  lastGameSession,
  isMobile,
  onRejoin,
  onStartNew,
  onCancel
}: {
  isOpen: boolean;
  lastGameSession: any;
  isMobile: boolean;
  onRejoin: () => void;
  onStartNew: () => void;
  onCancel: () => void;
}) => {
  if (!isOpen || !lastGameSession) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-gray-800 rounded-lg p-6 w-full mx-4 border border-gray-600 ${isMobile ? 'max-w-sm' : 'max-w-md'}`}>
        <h3 className="text-xl font-bold text-white mb-4 text-center">
          🎉 Resume Your Game?
        </h3>
        
        <div className="text-center mb-6">
          <p className="text-gray-300 mb-2">
            You were in a game that might still be active:
          </p>
          
          <div className="bg-gray-700 rounded-lg p-4 mb-4">
            <div className="text-white font-semibold">Room: {lastGameSession.roomId}</div>
            <div className="text-gray-300">Player: {lastGameSession.playerName}</div>
            <div className="text-sm text-gray-400 mt-1">
              {lastGameSession.gameInProgress ? '🎮 Game in progress' : '🎯 Waiting in lobby'}
            </div>
          </div>
        </div>
        
        <div className="space-y-3">
          <button
            onClick={onRejoin}
            className="w-full py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors"
          >
            🔄 Rejoin Game
          </button>
          
          <button
            onClick={onStartNew}
            className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
          >
            🆕 Start New Game
          </button>
          
          <button
            onClick={onCancel}
            className="w-full py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-semibold transition-colors"
          >
            ❌ Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Development Banner Component
 */
export const DevelopmentBanner = () => (
  <div className="fixed top-0 left-0 right-0 bg-yellow-600 text-black text-center py-2 text-sm font-medium z-40">
    ⚠️ DEVELOPMENT VERSION - P2P Features in Testing ⚠️
  </div>
);

/**
 * Reconnection Overlay Component
 */
export const ReconnectionOverlay = ({ isReconnecting }: { isReconnecting: boolean }) => {
  if (!isReconnecting) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-40">
      <div className="bg-gray-800 p-6 rounded-lg border border-gray-600 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-white mb-2">Connection Lost</p>
        <p className="text-gray-400 text-sm">Attempting to reconnect...</p>
      </div>
    </div>
  );
};